import { observer } from 'mobx-react'
import { FC, useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { Header, LoadingSpinner } from '@components/Generic'
import { forceRedirect } from '@/methods/forceRedirect'
import { isLocalDev } from '@/constants/envs'

type EidProviderText = { name: string; country: string }

interface GetContextResponse {
  providers: string[]
  allowMethodSelection: boolean
  userPii?: { firstName?: string; lastName?: string }
}

const ProviderIcon: FC<{ providerId: string }> = ({ providerId }) => {
  switch (providerId) {
    case 'ftn':
      return (
        <svg
          aria-hidden="true"
          className="eid-method-selection__logo"
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="31"
          viewBox="0 0 30 31"
          fill="none"
        >
          <rect y="0.115234" width="30" height="30" rx="2" fill="#DEDEDE" />
          <mask
            id="mask0_381_521"
            className="eid-method-selection__mask--alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="28"
            height="28"
          >
            <rect x="1.68066" y="1.64453" width="26.6394" height="26.6394" rx="1" fill="#003755" />
          </mask>
          <g mask="url(#mask0_381_521)">
            <path
              d="M36.6048 30.3359H-1.94043L-1.94012 0.356977H36.6051L36.6048 30.3359Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9.62725 17.8068V29.502H15.4057V17.8068H29.1171V12.1076H15.4057V0.286556H9.62725V12.1076H-0.00341797V17.8068H9.62725Z"
              fill="#002F6C"
            />
          </g>
        </svg>
      )
    case 'mitid':
      return (
        <svg
          aria-hidden="true"
          className="eid-method-selection__logo"
          xmlns="http://www.w3.org/2000/svg"
          width="40"
          height="41"
          viewBox="0 0 40 41"
          fill="none"
        >
          <path
            d="M5 10.1152C5 7.35381 7.23858 5.11523 10 5.11523H30C32.7614 5.11523 35 7.35381 35 10.1152V30.1152C35 32.8767 32.7614 35.1152 30 35.1152H10C7.23858 35.1152 5 32.8767 5 30.1152V10.1152Z"
            fill="#0060E6"
          />
          <path
            d="M22.6342 16.4756C26.1785 16.4756 27.6738 18.4139 27.6738 20.7952C27.6738 23.1765 26.1785 25.1148 22.6342 25.1148H21.1943V16.4756H22.6342Z"
            fill="white"
          />
          <path
            d="M16.2658 16.3652C15.1029 16.3652 14.1614 17.3067 14.1614 18.4697C14.1614 19.6326 15.1029 20.5741 16.2658 20.5741C17.4288 20.5741 18.3702 19.6326 18.3702 18.4697C18.3702 17.3067 17.4288 16.3652 16.2658 16.3652ZM16.2658 21.3494C14.3276 21.3494 12.7215 22.457 12.5 25.1152H20.087C19.8655 22.457 18.2595 21.3494 16.2658 21.3494Z"
            fill="white"
          />
        </svg>
      )
    case 'nbid':
      return (
        <svg
          aria-hidden="true"
          className="eid-method-selection__logo"
          xmlns="http://www.w3.org/2000/svg"
          width="40"
          height="41"
          viewBox="0 0 40 41"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.8125 10.584C7.03585 10.584 6.40625 11.2136 6.40625 11.9902C6.40625 12.7669 7.03585 13.3965 7.8125 13.3965H11.875C12.6517 13.3965 13.2812 12.7669 13.2812 11.9902C13.2812 11.2136 12.6517 10.584 11.875 10.584H7.8125ZM28.125 10.584C27.3483 10.584 26.7188 11.2136 26.7188 11.9902C26.7188 12.7669 27.3483 13.3965 28.125 13.3965H32.1875C32.9642 13.3965 33.5938 12.7669 33.5938 11.9902C33.5938 11.2136 32.9642 10.584 32.1875 10.584H28.125ZM6.40625 22.8235C6.40625 22.0469 7.03585 21.4173 7.8125 21.4173H11.875C12.6517 21.4173 13.2812 22.0469 13.2812 22.8235C13.2812 23.6002 12.6517 24.2298 11.875 24.2298H7.8125C7.03585 24.2298 6.40625 23.6002 6.40625 22.8235ZM7.8125 26.834C7.03585 26.834 6.40625 27.4636 6.40625 28.2402C6.40625 29.0169 7.03585 29.6465 7.8125 29.6465H11.875C12.6517 29.6465 13.2812 29.0169 13.2812 28.2402C13.2812 27.4636 12.6517 26.834 11.875 26.834H7.8125ZM26.7188 28.2402C26.7188 27.4636 27.3483 26.834 28.125 26.834H32.1875C32.9642 26.834 33.5938 27.4636 33.5938 28.2402C33.5938 29.0169 32.9642 29.6465 32.1875 29.6465H28.125C27.3483 29.6465 26.7188 29.0169 26.7188 28.2402ZM28.125 16.0007C27.3483 16.0007 26.7188 16.6303 26.7188 17.4069C26.7188 18.1836 27.3483 18.8132 28.125 18.8132H32.1875C32.9642 18.8132 33.5938 18.1836 33.5938 17.4069C33.5938 16.6303 32.9642 16.0007 32.1875 16.0007H28.125ZM16.5625 17.4069C16.5625 16.6303 17.1921 16.0007 17.9688 16.0007H22.0312C22.8079 16.0007 23.4375 16.6303 23.4375 17.4069C23.4375 18.1836 22.8079 18.8132 22.0312 18.8132H17.9688C17.1921 18.8132 16.5625 18.1836 16.5625 17.4069ZM17.9688 21.4173C17.1921 21.4173 16.5625 22.0469 16.5625 22.8235C16.5625 23.6002 17.1921 24.2298 17.9688 24.2298H22.0312C22.8079 24.2298 23.4375 23.6002 23.4375 22.8235C23.4375 22.0469 22.8079 21.4173 22.0312 21.4173H17.9688Z"
            fill="#39134C"
          />
        </svg>
      )
    case 'sbid':
      return (
        <svg
          aria-hidden="true"
          className="eid-method-selection__logo eid-method-selection__logo--offset"
          width="30"
          height="29"
          viewBox="0 0 30 29"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.15077 24.722C9.08591 24.722 9.90905 25.101 9.75106 26.1103L9.57266 27.2489C9.50964 27.6512 9.52136 27.7716 9.9726 27.7806L9.51408 28.8501C8.71615 28.9031 8.33006 28.8197 8.13834 28.4778C7.71515 28.7366 7.2497 28.8652 6.75124 28.8652C5.84716 28.8652 5.53615 28.4029 5.61657 27.8866C5.65438 27.6428 5.79941 27.4008 6.02982 27.2032C6.5258 26.7781 7.75438 26.7168 8.23455 26.3913C8.27645 26.027 8.12805 25.8978 7.67538 25.8978C7.14657 25.8978 6.70509 26.0727 5.951 26.5809L6.13473 25.4036C6.79118 24.9341 7.42189 24.7218 8.15077 24.7218V24.722ZM13.8369 24.722C14.7053 24.722 15.1101 25.2693 14.9743 26.1718L14.5695 28.8652H13.0122L13.3475 26.6351C13.4082 26.2268 13.2943 26.0408 12.9875 26.0408C12.7404 26.0408 12.5173 26.1798 12.3002 26.4801L11.9414 28.8652H10.3846L10.9963 24.7902H12.5538L12.4736 25.3232C12.9657 24.8908 13.3427 24.7218 13.8369 24.7218V24.722ZM17.7525 23.3689L17.3638 26.0767L18.8508 24.6091H20.7692L18.8593 26.4235L20.3904 28.8652H18.4425L17.261 26.8986H17.2464L16.9646 28.8652H15.4118L16.1996 23.3689H17.7524H17.7525ZM3.82882 23.5381C5.11846 23.5381 5.43497 24.1892 5.34213 24.7855C5.26704 25.2702 4.93811 25.6256 4.36331 25.8601C5.08757 26.1323 5.36964 26.5631 5.2658 27.2285C5.13568 28.0684 4.4013 28.6962 3.44503 28.6962H0L0.801302 23.5381H3.82882ZM27.1475 23.5381C29.16 23.5381 29.7412 24.9902 29.5551 26.1851C29.3723 27.3573 28.4389 28.6962 26.6705 28.6962H23.7362L24.5407 23.5381H27.1475ZM23.6538 23.5381L22.8456 28.6962H21.0165L21.8249 23.5381H23.6538ZM8.12982 27.0587C7.7052 27.3247 6.92272 27.2789 6.83734 27.8253C6.79686 28.0837 6.95893 28.2729 7.21917 28.2729C7.47248 28.2729 7.78083 28.1669 8.021 28C8.00485 27.9094 8.01266 27.8104 8.04106 27.6283L8.12982 27.0587ZM2.75166 26.4652H2.16018L1.93562 27.9096H2.48166C3.08893 27.9096 3.44556 27.6676 3.52402 27.1608C3.59148 26.7295 3.34349 26.4652 2.75166 26.4652ZM26.6588 24.7625H26.1706L25.7483 27.4709H26.2289C27.1125 27.4709 27.5995 27.0396 27.744 26.1172C27.8496 25.4365 27.6422 24.7625 26.6588 24.7625ZM3.01615 24.324H2.49248L2.28213 25.6778H2.8058C3.39763 25.6778 3.67367 25.3677 3.731 24.9973C3.79207 24.6046 3.60816 24.324 3.01615 24.324ZM7.78757 2.7364C8.36254 2.7364 8.80651 2.85777 9.03728 3.07849C9.19828 3.23297 9.25828 3.43513 9.21071 3.66422C9.16455 3.88531 8.91958 4.17317 8.55213 4.43265C7.51083 5.1725 7.69118 5.93929 7.7716 6.1571C8.01266 6.81235 8.81751 7.1659 9.45124 7.1659H10.7968L9.54177 15.1642H9.56325C9.09603 18.1531 8.74846 20.4071 8.68402 20.832H1.23621C1.37893 19.8921 3.24302 7.94834 3.36106 7.16826H3.47556L3.86237 7.16917H3.95148L4.25254 7.1699L4.33172 7.17008L4.44177 7.17027H4.50763L4.53816 7.17045L4.61822 7.17063H4.70769L4.71799 7.17081H4.73148C5.62668 7.1659 6.4516 6.7523 6.83396 6.11671C7.20781 5.50022 7.06562 4.83933 6.46544 4.39134C6.26538 4.2425 6.03515 4.00103 6.08024 3.70225C6.14201 3.29884 6.82065 2.7364 7.78739 2.7364H7.78757ZM19.3702 0.115234C26.2264 0.115234 31.0072 3.16783 29.8184 10.7846C28.8572 16.9336 23.9652 20.8322 17.9766 20.8322H16.5568L17.5198 14.6265C19.9454 14.5989 21.9777 13.442 22.3976 10.7571C22.8485 7.86736 21.4166 6.62675 18.7519 6.60564C18.7525 6.60127 18.7525 6.59891 18.7525 6.59891H16.7927C16.3415 6.59891 15.7617 6.34507 15.6114 5.93638C15.5645 5.80974 15.469 5.35301 16.1996 4.83224C16.4801 4.6319 16.9672 4.22321 17.0695 3.73428C17.1531 3.3245 17.0343 2.92837 16.7419 2.64851C16.4089 2.3288 15.8526 2.15903 15.1335 2.15903C13.9326 2.15903 13.0141 2.89452 12.9098 3.5807C12.9032 3.62528 12.8989 3.67751 12.8989 3.73464C12.8989 4.00758 13.0037 4.40954 13.5019 4.78329C13.6848 4.92031 13.8619 5.12174 13.8619 5.39122C13.8619 5.51478 13.8247 5.65143 13.7336 5.80373C13.4482 6.27902 12.7855 6.60036 12.0825 6.60436L10.7968 6.60127L11.8093 0.115234H19.3702Z"
            fill="#183E4F"
          />
        </svg>
      )

    default:
      return <></>
  }
}

export const EidMethodSelectionPage = observer(() => {
  const store = useContext(StoreContext)
  const { eidMethodSelectionPage: trans } = store.TranslationsState.translations

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [context, setContext] = useState<GetContextResponse>()

  const getAvailableMethods = async () => {
    try {
      if (isLocalDev) {
        // mock scenario for local development
        setContext({
          providers: ['ftn', 'mitid', 'nbid', 'sbid'],
          allowMethodSelection: true,
          userPii: { firstName: 'John', lastName: 'Doe' },
        })
        return
      }

      const { data } = await axiosInstance.get<GetContextResponse>(
        API_URL + '/signing/get-context',
        {
          withCredentials: true,
        }
      )

      if (data?.allowMethodSelection) {
        !!data?.providers?.length && setContext(data)
      } else {
        const { data } = await axiosInstance.post(
          API_URL + '/signing/start-process',
          {},
          {
            withCredentials: true,
          }
        )

        data?.redirectUrl && forceRedirect(data?.redirectUrl)
      }
    } catch (error) {
      devLog(error)
    }
  }

  const handleProceed = async (providerId: string) => {
    try {
      setIsLoading(true)
      const { data } = await axiosInstance.post(
        API_URL + `/signing/start-process`,
        {},
        {
          withCredentials: true,
          params: {
            selectedMethod: providerId,
          },
        }
      )

      data?.redirectUrl && forceRedirect(data?.redirectUrl)
    } catch (error) {
      devLog(error)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    store.InterfaceState.setSendBeforeunloadEvent(false)

    void getAvailableMethods()
  }, [])

  if (!context?.providers.length || isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-10">
      <div className="eid-method-selection__centered-wrap">
        <Header className="m-0-0-15">{trans.header}</Header>

        {!!context?.userPii?.firstName && !!context?.userPii?.lastName && (
          <>
            <p className="generic__paragraph">{trans.paragraph1}</p>
            <div className="eid-method-selection__full-name-box">
              {context.userPii.firstName} {context.userPii.lastName}
            </div>
          </>
        )}

        <p className="generic__paragraph">{trans.paragraph2}</p>

        <div className="eid-method-selection__method-container">
          {context.providers.map((providerId) => {
            const provider = (trans.options as Partial<Record<string, EidProviderText>>)[providerId]
            return (
              <div
                className="eid-method-selection__method-item"
                key={providerId}
                onClick={() => handleProceed(providerId)}
              >
                <ProviderIcon providerId={providerId} />
                <div className="eid-method-selection__name">{provider?.name}</div>
                <div className="eid-method-selection__country">{provider?.country}</div>
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  fill="none"
                  className="eid-method-selection__arrow"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.54048 6.65522C9.79432 6.40138 10.2059 6.40138 10.4597 6.65522L15.4597 11.6552C15.7136 11.9091 15.7136 12.3206 15.4597 12.5745L10.4597 17.5745C10.2059 17.8283 9.79432 17.8283 9.54048 17.5745C9.28664 17.3206 9.28664 16.9091 9.54048 16.6552L14.0809 12.1148L9.54048 7.57446C9.28664 7.32062 9.28664 6.90907 9.54048 6.65522Z"
                    fill="#007CBE"
                  />
                </svg>
              </div>
            )
          })}
        </div>

        <div className="generic__spacer h-30" />
      </div>
    </div>
  )
})
